<template>
  <v-app id="submit-form-app">
    <v-form
      ref="form"
      class="submit-form"
    >


      <div class="event">
        <div class="event__date">
          <div class="date"></div>
        </div>
        <div class="event__main">
          <v-text-field
            class="event__title"
            label="Title"
            aria-label="Title"
            :value="title"
            @change="title = $event"
            counter="50"
            :rules="[
              v => !!v || 'Please provide a title',
              v => v.length < 50 || 'The maximum title length is 50 characters'
            ]"
            required
          ></v-text-field>
          <v-text-field
            class="event__location"
            label="Location"
            aria-label="Location"
            :value="location"
            @change="location = $event"
            counter="60"
            :rules="[
              v => !!v || 'Please provide a location',
              v => v.length < 60 || 'The maximum location length is 60 characters'
            ]"
            required
          ></v-text-field>
          <div class="event__detail">
            <v-text-field
              class="event__detail_left"
              label="Additional Detail"
              aria-label="Additional Detail"
              counter="50"
              :rules="[
                v => v.length < 50 || 'The maximum length is 50 characters'
              ]"
              :value="left_detail"
              @change="left_detail = $event"
            ></v-text-field>
            <div class="event__detail_right">
              <v-text-field
                label="Additional Detail"
                aria-label="Additional Detail"
                counter="50"
                :rules="[
                  v => v.length < 50 || 'The maximum length is 50 characters'
                ]"
                :value="right_detail"
                @change="right_detail = $event"
              ></v-text-field>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn class="event__additional-helper" text icon small v-on="on" label="About Additional Details"><v-icon>help_outline</v-icon></v-btn>
                </template>
                <span>Optional text to display below the location. You can use this for admission costs, a contact phone number, or a web address.</span>
              </v-tooltip>
            </div>
          </div>
          <div class="image-upload">
            <img
              :src="imagePreview"
              v-show="imagePresent"
            />
            <label for="image">Choose an image: </label>
            <input
              type="file"
              name="image"
              @change="image = $event.target.files[0]"
              accept="image/*"
              :rules="[
                v => !uploadPresent || !!imagePresent || 'Please provide an image file (jpg, png)'
              ]"
            />
          </div>
          <v-textarea
            class="event__description"
            label="Description"
            aria-label="Description"
            counter="900"
            :rules="[
              v => v.length < 900 || 'The maximum length for the description is 900 characters'
            ]"
            :value="description"
            @change="description = $event"
          >
          </v-textarea>

          <label class="occurrences__descr">If your event repeats every week, click 'Add multiple dates' to fill out all of your dates. Otherwise please add each date separately below.</label>
          <v-dialog
            v-model="recurringEventsDialogOpen"
            max-width="600"
            scrollable
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="occurrences__action"
                color="secondary"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>repeat</v-icon> Add multiple dates
              </v-btn>
            </template>

            <v-card>
              <v-card-title>Add multiple dates</v-card-title>
              <v-card-text style="height: 300px">
                <div
                  class="recurring-events-dialog__body"
                >
                  <v-form
                    ref="recurForm"
                  >
                    <p>My first event is on</p>
                    <Occurrence
                      v-model="recurStart"
                      :deletable="false"
                      :rules="[
                        v => (!!v && !!v.start && !!v.start.isValid && !!v.start.isValid()) || 'Please provide a date and start time',
                        v => !v.end || v.end && v.start.isBefore(v.end) || 'The start time must be before the end time'
                      ]"
                    />
                    <div class="d-flex flex-wrap align-baseline">
                      <p>and repeats every <em>{{ recurDay }}</em> for</p>
                      <v-text-field
                        hide-details
                        single-line
                        dense
                        type="number"
                        min="1"
                        max="52"
                        step="1"
                        class="recurring-events-dialog__count"
                        v-model="recurCount"
                      ></v-text-field>
                      <p>weeks.</p>
                    </div>
                    <div
                      v-if="recurStart.start && recurCount > 0"
                    >
                      <v-divider class="recurring-events-dialog__separator"></v-divider>
                      <p>Please check the following dates are correct and click 'Add dates' to add these to your submission.</p>
                      <ul>
                        <li
                          v-for="(occurrence, index) in recurOccurrencesDisplay"
                          :key="index"
                        >
                          {{ occurrence }}
                        </li>
                      </ul>
                    </div>
                  </v-form>
                </div>
              </v-card-text>
              <v-card-actions class="recurring-events-dialog__actions">
                <v-btn
                  color="primary"
                  @click="addRecurringOccurences"
                >
                  Add dates
                </v-btn>
                <v-btn
                  color="cancel"
                  @click="recurringEventsDialogOpen = false"
                >
                  Cancel
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <div class="occurrences">
            <Occurrence
              v-for="(occurrence, index) in occurrences"
              :key="index"
              v-model="occurrences[index]"
              :deletable="index > 0"
              @delete="occurrences.splice(index, 1)"
              :rules="[
                v => (!!v && !!v.start && !!v.start.isValid && !!v.start.isValid()) || 'Please provide a date and start time',
                v => !v.end || v.end && v.start.isBefore(v.end) || 'The start time must be before the end time'
              ]"
              required
            />
            <v-btn
              class="occurrences__action"
              color="secondary"
              @click="addOccurrence"
            >
              <v-icon>add</v-icon> Add one more date
            </v-btn>
          </div>


          <div class="contact-details">
            <p class="submit-form__contact-intro">Please provide your contact details so we can contact you about your submission. Your details will not be listed publicly.</p>
            <v-text-field
              label="Name"
              aria-label="Name"
              :value="name"
              @change="name = $event"
              :rules="[v => !!v || 'Please provide a name']"
              required
            />
            <v-text-field
              label="Email"
              aria-label="Email"
              type="email"
              :value="email"
              @change="email = $event"
              :rules="[
                v => !!v || 'Please provide an email',
                v => !!v.match(/^[^@\s]+@[^@\s]+\.[^@\s]+$/) || 'Please provide a valid email'
              ]"
              required
            />
            <v-text-field
              label="Phone"
              aria-label="Phone"
              type="tel"
              :value="phone"
              @change="phone = $event"
            />
          </div>

          <v-checkbox
            label="Is this a commerical or for-profit event?"
            :value="commercial_event"
            @change="commercial_event = $event"
          />

          <div class="messages">
            <v-alert v-show="success" type="success">
              Thanks for sending your event! Once it has been approved, it will appear in the listings.
            </v-alert>
            <v-alert v-show="!valid" type="warning">
              Something's not right, please check your submission.
            </v-alert>
            <v-alert v-show="erroring" type="error">
              Sorry, something went wrong. Please try again or <a href="//radioleyland.co.uk/contact-us" target="_blank">get in touch.</a>
            </v-alert>
          </div>

          <v-btn
            class="event__submit"
            color="primary"
            @click="submit"
            :disabled="submitting"
            :loading="submitting"
          >
            <span>Submit</span>
          </v-btn>
        </div>
        <div class="event__image">
          <div class="event__image-placeholder"></div>
        </div>
      </div>

    </v-form>
  </v-app>
</template>

<script>
import { range } from 'ramda';
import moment from 'moment';
import Occurrence from './Occurrence';

export default {
  name: 'submit-form',
  components: {
    Occurrence
  },
  data() {
    return {
      title: '',
      location: '',
      left_detail: '',
      right_detail: '',
      description: '',
      image: [],
      occurrences: [ {} ],
      name: '',
      email: '',
      phone: '',
      commercial_event: false,

      recurringEventsDialogOpen: false,
      recurStart: {},
      recurCount: 1,

      submitting: false,
      success: false,
      valid: true,
      erroring: false,
    };
  },
  computed: {
    uploadPresent() {
      return this.image instanceof File;
    },
    imagePresent() {
      return this.uploadPresent && this.image.type && /^image\//.test(this.image.type);
    },
    imagePreview() {
      return this.imagePresent ? URL.createObjectURL(this.image) : undefined;
    },
    recurDay() {
      return this.recurStart.start ? moment.utc(this.recurStart.start).format('dddd') : '_';
    },
    recurOccurrences() {
      const format = "Do MMMM YYYY h:mma"; // TODO export from Occurrence
      return range(0, Number(this.recurCount)).map(
        i => ({
          start: moment.utc(this.recurStart.start).add(7 * i, 'days'),
          end: this.recurStart.end && moment.utc(this.recurStart.end).add(7 * i, 'days')
        })
      )
    },
    recurOccurrencesDisplay() {
      return this.recurOccurrences.map(
        occurrence => moment.utc(occurrence.start).format('Do MMMM YYYY')
      );
    }
  },
  methods: {
    getFormattedOccurrences() {
      const formatString = 'YYYY-MM-DD HH:mm:ss';
      return this.occurrences.map(
        occurrence => ({
          start: occurrence.start && occurrence.start.format(formatString),
          end: occurrence.end && occurrence.end.format(formatString),
        })
      );
    },
    addOccurrence() {
      const next = {
        start: this.occurrences[this.occurrences.length - 1].start,
        end: this.occurrences[this.occurrences.length - 1].end
      };

      if (next.start) {
        next.start = next.start.clone();
        next.start.add(1, 'days');
      }

      if (next.end) {
        next.end = next.end.clone();
        next.end.add(1, 'days');
      }

      this.occurrences.push(next);
    },
    addRecurringOccurences() {
      if (this.$refs.recurForm.validate()) {
        this.occurrences.push(...this.recurOccurrences);
        this.occurrences = this.occurrences.filter(occurrence => occurrence.start);
        this.recurringEventsDialogOpen = false;
        this.recurStart = {};
        this.recurCount = 1;
      }
    },
    async submit() {
      this.submitting = true;
      this.success = false;
      this.valid = true;
      this.erroring = false;

      if (!this.$refs.form.validate()) {
        this.submitting = false;
        this.success = false;
        this.valid = false;
        this.erroring = false;
        return;
      }

      const fileEndpoint = `//${process.env.VUE_APP_DIRECTUS_API_HOST}/_/files`;
      const eventsEndpoint = `//${process.env.VUE_APP_DIRECTUS_API_HOST}/_/items/events`;

      try {
        let imageId = null;

        if (this.imagePresent) {
          const imageForm = new FormData();
          imageForm.append('image', this.image);

          const imageReq = await fetch(fileEndpoint, {
            method: 'POST',
            mode: 'cors',
            cache: 'no-cache',
            body: imageForm
          });
          const imageRes = await imageReq.json();

          if (imageRes.error) {
            throw imageRes.error;
          }

          imageId = imageRes.data.id;
        }

        const data = {
          status: 'draft',
          title: this.title,
          location: this.location,
          left_detail: this.left_detail,
          right_detail: this.right_detail,
          description: this.description,
          image: imageId,
          occurrences: this.getFormattedOccurrences(),
          name: this.name,
          email: this.email,
          phone: this.phone,
          commercial_event: this.commercial_event,
        }

        const eventReq = await fetch(eventsEndpoint, {
          method: 'POST',
          mode: 'cors',
          cache: 'no-cache',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        });
        const eventRes = await eventReq.json();

        if (eventRes.error) {
          throw eventRes.error;
        }

        this.submitting = false;
        this.success = true;
        this.valid = true;
        this.erroring = false;
      } catch (e) {
        this.submitting = false;
        this.success = false;
        this.valid = true;
        this.erroring = true;
        // eslint-disable-next-line
        console.error(e);
      }
    }
  },
};
</script>

<style lang="scss">
.recurring-events-dialog {
  &__body {
    margin: 0 1.5rem;
  }

  &__count {
    flex: 0 1 3rem;
    padding-top: 0;
    margin: 0 0.5rem;
  }

  &__separator {
    margin: 1rem;
  }

  &__actions {
    margin-top: 1rem;
  }
}

.submit-form {
  @import './event';

  .occurrences__descr {
    display: block;
    margin-bottom: 1rem;
    font-weight: 500;
  }

  .occurrences__action {
    font-weight: 400;
    margin: 0 1rem 1rem 0;
  }

  .event__submit {
    margin-right: 1rem;
  }

  .event__detail_right {
    display: flex;
    align-items: center;
    margin-right: 0;
    flex: 7;
  }

  .event__additional-helper {
    width: 2rem;
    height: 2rem;
    margin-left: 0.5rem;
  }

  &__contact-intro {
    margin: 1rem 0;
  }

  .messages {
    a, :any-link {
      color: #fff;
      text-decoration: underline;
    }
  }

  .image-upload {
    img {
      width: 100%;
    }
  }

  .event__description.v-textarea textarea {
    line-height: $line-height-root !important;
  }
}
</style>
